import {
  BadgeCheck,
  BookOpen,
  BookOpenCheck,
  Code2,
  GitCommit,
  Keyboard,
  Lock,
  Map,
  Play,
  Route,
} from "lucide-react";
import { type MetaFunction, useLoaderData } from "react-router";

import { Badge } from "@/ui/badge";

import { CourseFooter } from "./course-footer";
import { CourseShell } from "./course-shell";
import type { GuideLoaderResponse } from "./types";

export const guideMeta: MetaFunction = () => {
  return [
    { title: `How it works | ${publicEnv.siteTitle}` },
    {
      name: "description",
      content:
        "Learn how the Makefile FYI course, concept guide, repository, and interactive exercises work together.",
    },
  ];
};

export function CourseGuidePage() {
  const { dbUser } = useLoaderData<GuideLoaderResponse>();

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-12 pb-20 sm:w-[calc(100%-3rem)] sm:gap-16 sm:pb-28">
        <header className="grid max-w-3xl gap-7 pt-10 sm:pt-14 lg:pt-16">
          <Badge className="w-fit font-mono shadow-xs" variant="secondary">
            <Map />
            How it works
          </Badge>
          <div className="grid gap-4">
            <h1 className="text-4xl leading-tight font-semibold text-balance sm:text-5xl">
              Learn through three connected views
            </h1>
            <p className="text-muted-foreground max-w-2xl text-lg leading-8 text-pretty">
              Makefile FYI combines a guided course, a topic-oriented concept
              guide, and the complete repository history. Move between them as
              often as your questions change.
            </p>
          </div>
        </header>

        <section aria-labelledby="guide-path-title" className="grid gap-6">
          <div className="grid max-w-2xl gap-2">
            <h2 className="text-2xl font-semibold" id="guide-path-title">
              Choose the path that fits
            </h2>
            <p className="text-muted-foreground leading-7">
              There is no required way to use all three views.
            </p>
          </div>

          <div className="border-border divide-border divide-y border-y">
            <GuidePath
              description="Start in Course and follow the core modules in order. Use the appendix as a supporting reference, and open a Concept when an underlying Make, shell, or repository model is unfamiliar."
              icon={BookOpen}
              title="New to production Makefiles"
            />
            <GuidePath
              description="Resume the Course from your saved device progress, or revisit the Concepts linked from the last lesson you completed."
              icon={Route}
              title="Returning learner"
            />
            <GuidePath
              description="Search Concepts by topic and use Repository to compare concrete source snapshots without following every lesson."
              icon={GitCommit}
              title="Experienced Make user"
            />
          </div>
        </section>

        <section
          aria-labelledby="guide-interactions-title"
          className="grid gap-6"
        >
          <div className="grid max-w-2xl gap-2">
            <h2
              className="text-2xl font-semibold"
              id="guide-interactions-title"
            >
              Interaction guide
            </h2>
            <p className="text-muted-foreground leading-7">
              The course uses the same small set of interactions throughout.
            </p>
          </div>

          <dl className="grid gap-3 md:grid-cols-2">
            <GuideInteraction
              description="Focused lines explain one decision. Make and shell clips can hide full-line comments; View in Repository opens the complete file and preserves the selected range."
              icon={Code2}
              term="Code clips"
            />
            <GuideInteraction
              description="Select the command line, type or auto-fill the expected command, and press Enter or Run to advance."
              icon={Play}
              term="Interactive replays"
            />
            <GuideInteraction
              description="Hover or focus a referenced term for a short explanation, then open its full Concept page when needed."
              icon={BookOpenCheck}
              term="Concept references"
            />
            <GuideInteraction
              description="Use History to understand a commit’s position and Files to inspect the source snapshot at that exact point."
              icon={Keyboard}
              term="Repository explorer"
            />
          </dl>
        </section>

        <section
          aria-labelledby="guide-access-title"
          className="border-border/70 grid gap-6 border-y py-8 sm:py-10"
        >
          <div className="grid max-w-2xl gap-2">
            <h2 className="text-2xl font-semibold" id="guide-access-title">
              Access and progress
            </h2>
            <p className="text-muted-foreground leading-7">
              You can understand what is available before opening it.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
              <BadgeCheck className="text-success mt-1 size-5" />
              <div className="grid gap-1">
                <h3 className="font-semibold">Free foundations</h3>
                <p className="text-muted-foreground text-sm leading-6">
                  The first lesson in every core module and in the appendix is
                  free. Each concept section also begins with a free foundation.
                  Titles and summaries remain visible throughout.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
              <Lock className="text-muted-foreground mt-1 size-5" />
              <div className="grid gap-1">
                <h3 className="font-semibold">One full-access entitlement</h3>
                <p className="text-muted-foreground text-sm leading-6">
                  Full access unlocks paid lessons, concepts, source snapshots,
                  interactive replays, and future additions.
                </p>
              </div>
            </div>
          </div>

          <p className="text-muted-foreground text-sm leading-6">
            Lesson completion and your most recently visited core lesson are
            stored on this device. Core progress helps the Course suggest where
            to continue, while appendix completion is tracked separately and the
            Concepts and Repository remain non-linear.
          </p>
        </section>
      </main>

      <CourseFooter />
    </CourseShell>
  );
}

function GuidePath({
  description,
  icon: Icon,
  title,
}: {
  description: string;
  icon: typeof BookOpen;
  title: string;
}) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-4 py-5">
      <span className="bg-secondary text-secondary-foreground ring-border/70 flex size-10 items-center justify-center rounded-md shadow-xs ring-1">
        <Icon className="size-4" />
      </span>
      <div className="grid gap-1.5">
        <h3 className="font-semibold">{title}</h3>
        <p className="text-muted-foreground max-w-3xl text-sm leading-6">
          {description}
        </p>
      </div>
    </div>
  );
}

function GuideInteraction({
  description,
  icon: Icon,
  term,
}: {
  description: string;
  icon: typeof BookOpen;
  term: string;
}) {
  return (
    <div className="bg-card grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 rounded-md border p-4 shadow-sm">
      <Icon className="text-muted-foreground mt-0.5 size-4" />
      <div className="grid gap-1.5">
        <dt className="font-semibold">{term}</dt>
        <dd className="text-muted-foreground text-sm leading-6">
          {description}
        </dd>
      </div>
    </div>
  );
}
